import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

// Sous-hook Messagerie : gestion des dossiers (CRUD), du drag & drop d'emails,
// et du changement de dossier courant.
// Dépendances transverses reçues en arguments (état/handlers détenus par useMailHub).
export function useMailFolders({
  selectedAccount, backendUrl, authToken,
  loadEmails, loadAccountStats,
  setCurrentFolder, setEmailsPage,
  selectedFolderId, setSelectedFolderId
}) {
  const [emailFolders, setEmailFolders] = useState([]);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [draggedEmailId, setDraggedEmailId] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [folderMenuOpen, setFolderMenuOpen] = useState(null);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');

  const loadEmailFolders = React.useCallback(async () => {
    if (!selectedAccount) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/folders?account_id=${selectedAccount.id}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        setEmailFolders(data);
      }
    } catch (error) {
      logger.error('Error loading email folders:', error);
    }
  }, [selectedAccount, backendUrl, authToken]);

  // Créer un nouveau dossier
  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/folders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newFolderName.trim(),
          account_id: selectedAccount?.id
        })
      });

      if (response.ok) {
        toast.success('Dossier créé');
        setNewFolderName('');
        setShowCreateFolderModal(false);
        loadEmailFolders();
      }
    } catch (error) {
      logger.error('Error creating folder:', error);
      toast.error('Erreur lors de la création du dossier');
    }
  };

  // Renommer un dossier
  const renameFolder = async () => {
    if (!folderToRename || !renameFolderName.trim()) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/folders/${folderToRename.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: renameFolderName.trim() })
      });

      if (response.ok) {
        toast.success('Dossier renommé');
        setShowRenameFolderModal(false);
        setFolderToRename(null);
        setRenameFolderName('');
        loadEmailFolders();
      }
    } catch (error) {
      logger.error('Error renaming folder:', error);
      toast.error('Erreur lors du renommage');
    }
  };

  // Supprimer un dossier
  const deleteFolder = async (folderId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/folders/${folderId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        toast.success('Dossier supprimé');
        if (selectedFolderId === folderId) {
          setSelectedFolderId(null);
        }
        loadEmailFolders();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      logger.error('Error deleting folder:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Ouvrir le modal de renommage
  const openRenameModal = (folder) => {
    setFolderToRename(folder);
    setRenameFolderName(folder.name);
    setShowRenameFolderModal(true);
    setFolderMenuOpen(null);
  };

  // Déplacer un email vers un dossier
  const moveEmailToFolder = async (emailId, folderId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/emails/${emailId}/move-to-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ folder_id: folderId })
      });

      if (response.ok) {
        toast.success('Email déplacé');
        loadEmails();
        loadEmailFolders();
        loadAccountStats();
      }
    } catch (error) {
      logger.error('Error moving email:', error);
      toast.error('Erreur lors du déplacement');
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, emailId) => {
    setDraggedEmailId(emailId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', emailId);
  };

  const handleEmailDragEnd = () => {
    setDraggedEmailId(null);
    setDragOverFolderId(null);
  };

  const handleFolderDragOver = (e, folderId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolderId(folderId);
  };

  const handleFolderDragLeave = () => {
    setDragOverFolderId(null);
  };

  const handleFolderDrop = (e, folderId) => {
    e.preventDefault();
    const emailId = e.dataTransfer.getData('text/plain');
    if (emailId && folderId) {
      moveEmailToFolder(emailId, folderId);
    }
    setDraggedEmailId(null);
    setDragOverFolderId(null);
  };

  const changeFolder = (folder) => {
    setCurrentFolder(folder);
    setEmailsPage(1);
    // Réinitialiser le dossier personnalisé quand on change de dossier système
    setSelectedFolderId(null);
  };

  // Fermer le menu dossier quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = () => setFolderMenuOpen(null);
    if (folderMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [folderMenuOpen]);

  return {
    emailFolders, setEmailFolders,
    showCreateFolderModal, setShowCreateFolderModal,
    newFolderName, setNewFolderName,
    draggedEmailId, setDraggedEmailId,
    dragOverFolderId, setDragOverFolderId,
    folderMenuOpen, setFolderMenuOpen,
    showRenameFolderModal, setShowRenameFolderModal,
    folderToRename, setFolderToRename,
    renameFolderName, setRenameFolderName,
    loadEmailFolders, createFolder, renameFolder, deleteFolder, openRenameModal,
    moveEmailToFolder, handleDragStart, handleEmailDragEnd,
    handleFolderDragOver, handleFolderDragLeave, handleFolderDrop, changeFolder
  };
}
